import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, publishLast, refCount } from 'rxjs/operators';
import { deserialize } from 'santee-dcts';
import { Settings } from '../../core';
import { Protocol, ProtocolsReadModel } from './protocol';
import { ActivityLogger } from '../../activities/activity-logger';
import { ProjectReadModel, Project } from './project';

@Injectable()
export class AdminProtocolsStorage {
    private readonly protocols: Observable<Protocol[]>;

    constructor(
        private readonly http: HttpClient,
        private readonly settings: Settings,
        private readonly activityLogger: ActivityLogger,
    ) {
        this.protocols = this.loadAll()
            .pipe(
                publishLast(),
                refCount()
            );
    }

    public get all(): Observable<Protocol[]> {
        return this.protocols;
    }

    public search(searchTerm: string): Observable<Protocol[]> {
        return searchTerm
            ? this.http.get(`${this.settings.apiUrl}/admin/protocols`, { params: { searchTerm } })
                .pipe(map(protocols => deserialize(protocols, ProtocolsReadModel).protocols))
            : of([]);
    }

    public searchById(protocolId: string): Observable<Protocol[]> {
        if (!protocolId) {
            return of([]);
        }

        return this.http.get(`${this.settings.apiUrl}/admin/protocols/${protocolId}`)
                .pipe(map(protocol => [deserialize(protocol, Protocol)]));
    }

    public updateAccessibility(protocolId: number, isAccessible: boolean): Observable<void> {
        this.activityLogger.logActivity('Change Protocol Access');

        return this.http.patch<void>(`${this.settings.apiUrl}/admin/protocols/${protocolId}`, { isAccessible } );
    }

    public getProtocolProjects(protocolId: number): Observable<Project[]> {
        return this.http.get(`${this.settings.apiUrl}/admin/protocols/${protocolId}/projects`)
            .pipe(map(projects => deserialize(projects, ProjectReadModel).projects));
    }

    private loadAll(): Observable<Protocol[]> {
        return this.http.get(`${this.settings.apiUrl}/admin/protocols`)
            .pipe(map(rawData => deserialize(rawData, ProtocolsReadModel).protocols));
    }
}